import { apiClient } from './api';

export interface Product {
  id: string;
  name: string;
  brand: string;
  category: string;
  sku: string;
  description?: string;
  costPrice: number;
  sellingPrice: number;
  currentStock: number;
  minStockLevel: number;
  maxStockLevel: number;
  unit: string;
  expiryDate?: string;
  supplierId?: string;
  isActive: boolean;
  salonId: string;
  createdAt: string;
  updatedAt: string;
  supplier?: {
    id: string;
    name: string;
    contactPerson?: string;
  };
  transactions?: StockTransaction[];
  profitMargin?: number;
  stockValue?: number;
  isLowStock?: boolean;
  isExpiringSoon?: boolean;
  daysUntilExpiry?: number;
}

export interface StockTransaction {
  id: string;
  productId: string;
  type: 'PURCHASE' | 'USAGE' | 'ADJUSTMENT' | 'RETURN';
  quantity: number;
  unitCost?: number;
  totalCost?: number;
  reason: string;
  referenceId?: string;
  performedBy: string;
  createdAt: string;
}

export interface CreateProductData {
  name: string;
  brand: string;
  category: string;
  description?: string;
  costPrice: number;
  sellingPrice: number;
  currentStock: number;
  minStockLevel: number;
  maxStockLevel: number;
  unit: string;
  expiryDate?: string;
  supplierId?: string;
}

export interface UpdateProductData {
  name?: string;
  brand?: string;
  category?: string;
  description?: string;
  costPrice?: number;
  sellingPrice?: number;
  currentStock?: number;
  minStockLevel?: number;
  maxStockLevel?: number;
  unit?: string;
  expiryDate?: string;
  supplierId?: string;
}

export interface StockAdjustmentData {
  quantity: number;
  type: 'PURCHASE' | 'USAGE' | 'ADJUSTMENT' | 'RETURN';
  reason: string;
  unitCost?: number;
  referenceId?: string;
}

export interface ProductFilters {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  brand?: string;
  isActive?: boolean;
  lowStock?: boolean;
  expiringSoon?: boolean;
}

export interface ProductResponse {
  products: Product[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface ProductStats {
  totalProducts: number;
  activeProducts: number;
  inactiveProducts: number;
  lowStockCount: number;
  expiringCount: number;
  totalStockValue: number;
  categoryBreakdown: {
    category: string;
    count: number;
  }[];
}

export interface CategoryBrand {
  category?: string;
  brand?: string;
  count: number;
}

class ProductService {
  private baseUrl = '/products';

  async getProducts(filters: ProductFilters = {}): Promise<ProductResponse> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });

    const response = await apiClient.get(`${this.baseUrl}?${params.toString()}`);
    return response;
  }

  async getProductById(id: string): Promise<Product> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response;
  }

  async createProduct(data: CreateProductData): Promise<Product> {
    const response = await apiClient.post(this.baseUrl, data);
    return response;
  }

  async updateProduct(id: string, data: UpdateProductData): Promise<Product> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return response;
  }

  async adjustStock(
    id: string,
    data: StockAdjustmentData
  ): Promise<{ product: Product; transaction: StockTransaction }> {
    const response = await apiClient.post(`${this.baseUrl}/${id}/adjust-stock`, data);
    return response;
  }

  async toggleProductStatus(id: string): Promise<Product> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/toggle-status`);
    return response;
  }

  async deleteProduct(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  async getProductCategories(): Promise<CategoryBrand[]> {
    const response = await apiClient.get(`${this.baseUrl}/categories`);
    return response;
  }

  async getProductBrands(): Promise<CategoryBrand[]> {
    const response = await apiClient.get(`${this.baseUrl}/brands`);
    return response;
  }

  async getLowStockProducts(): Promise<Product[]> {
    const response = await apiClient.get(`${this.baseUrl}/low-stock`);
    return response;
  }

  async getExpiringProducts(days: number = 30): Promise<Product[]> {
    const response = await apiClient.get(`${this.baseUrl}/expiring?days=${days}`);
    return response;
  }

  async getProductStats(): Promise<ProductStats> {
    const response = await apiClient.get(`${this.baseUrl}/stats`);
    return response;
  }
}

export const productService = new ProductService();
